import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  DatePicker,
  Button,
  Space,
  Table,
  message,
  Row,
  Col,
  Input,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;

export default function LaporanMutasiBarang() {
  const [selectedMonth, setSelectedMonth] = useState(dayjs()); // default: bulan ini
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page, pageSize, month = dayjs(), search = "") => {
      setLoading(true);
      try {
        const user = getUser();
        const userLevel = String(user?.id_user_level || user?.id_level || "");

        const params = {
          page,
          limit: pageSize,
          year: month.year(),
          month: month.month() + 1, // 1-based
        };

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (userLevel === "7") {
          params.id_customer = user.username;
        }

        const res = await axiosInstance.get("/report/mutasi-barang", {
          params,
        });

        setData(res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          current: res.data.page,
          pageSize: res.data.limit,
          total: res.data.total,
        }));
      } catch (e) {
        console.error(e);
        message.error("Gagal load laporan mutasi barang");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(1, 10, selectedMonth, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData(1, pagination.pageSize, selectedMonth, searchText);
  };

  const handleReset = () => {
    const now = dayjs();
    setSelectedMonth(now);
    setSearchText("");
    fetchData(1, 10, now, "");
  };

  const handleTableChange = (newPagination) => {
    fetchData(
      newPagination.current,
      newPagination.pageSize,
      selectedMonth,
      searchText,
    );
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const user = getUser();
      const userLevel = String(user?.id_user_level || user?.id_level || "");

      const params = {
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
      };

      if (searchText && searchText.trim()) {
        params.search = searchText.trim();
      }

      if (userLevel === "7") {
        params.id_customer = user.username;
      }

      const response = await axiosInstance.get("/report/mutasi-barang/export", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `laporan_mutasi_${String(selectedMonth.year())}_${String(selectedMonth.month() + 1).padStart(2, "0")}.xlsx`;
      link.click();
      message.success("Export berhasil");
    } catch (err) {
      console.error(err);
      message.error("Gagal mengunduh file excel");
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 45,
      align: "center",
      fixed: "left",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "Kode", dataIndex: "item_code", width: 100, ellipsis: true },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      width: 180,
      ellipsis: true,
    },
    { title: "Satuan", dataIndex: "satuan", width: 60, align: "center" },
    {
      title: "Saldo Awal",
      dataIndex: "saldo_awal",
      align: "right",
      width: 90,
      render: (val) => Number(val || 0).toLocaleString("id-ID"),
    },
    {
      title: "Masuk",
      dataIndex: "jumlah_masuk",
      align: "right",
      width: 75,
      render: (val) => (
        <span style={{ color: "#389e0d", fontWeight: 600 }}>
          {Number(val || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Keluar",
      dataIndex: "jumlah_keluar",
      align: "right",
      width: 75,
      render: (val) => (
        <span
          style={{
            color: Number(val) > 0 ? "#cf1322" : undefined,
            fontWeight: 600,
          }}
        >
          {Number(val || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Adj",
      dataIndex: "penyesuaian",
      align: "right",
      width: 60,
      responsive: ["md"],
      render: (val) => Number(val || 0).toLocaleString("id-ID"),
    },
    {
      title: "Saldo Akhir",
      dataIndex: "saldo_akhir",
      align: "right",
      width: 90,
      render: (val) => (
        <span style={{ color: "#096dd9", fontWeight: 700 }}>
          {Number(val || 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      title: "Pencacahan",
      align: "right",
      width: 90,
      responsive: ["lg"],
      render: () => "-",
    },
    {
      title: "Selisih",
      align: "right",
      width: 70,
      responsive: ["lg"],
      render: () => "-",
    },
    { title: "Ket", width: 80, responsive: ["xl"], render: () => "-" },
  ];

  return (
    <Card bodyStyle={{ padding: "16px" }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Title level={4} style={{ margin: 0 }}>
            Laporan Mutasi Barang
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Per Bulan: {selectedMonth.format("MMMM YYYY")}
          </Text>
        </Col>
        <Col xs={24} md={12}>
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Input
              placeholder="Cari kode/nama..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ width: 160 }}
              prefix={<SearchOutlined />}
            />
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(val) => setSelectedMonth(val || dayjs())}
              format="MMMM YYYY"
              allowClear={false}
              style={{ width: 160 }}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Cari
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              loading={exportLoading}
              disabled={exportLoading}
            >
              {exportLoading ? "Membuat file..." : "Excel"}
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record, index) => `${record.item_code}-${index}`}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (total) => `Total ${total} item`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        scroll={{ x: 900 }}
      />
    </Card>
  );
}
